import { useCallback, useEffect, useState, type JSX, type ReactNode } from 'react';
import {
  fetchAuthConfig,
  fetchAuthMe,
  postLogin,
  postLogout,
  setUnauthorizedHandler,
  type AuthUser,
} from '../api';
import { AuthContext, type AuthState, type AuthStatus } from './AuthContext';

/**
 * Resolves the live operator identity for the tree (RBAC R.2b, #314 — replaces the
 * R.1 seat shell). On boot it reads the server's enforcement flag (GET
 * /api/auth/config) and the current session (GET /api/auth/me) in parallel, then
 * exposes `{ user, authEnforced, status, login, logout, refresh }` via context.
 *
 * Wrapping the root is invisible when AUTH_ENFORCED ships OFF: config returns
 * `{ authEnforced: false }`, so the root gate always renders the COP (non-breaking).
 * A login is still reachable/testable; R.2c flipping the server flag makes the gate
 * enforce automatically. Resilient by design — if the API is unreachable the boot
 * probe degrades to `authEnforced: false` + no user, so the COP still boots.
 */
export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [authEnforced, setAuthEnforced] = useState(false);
  const [status, setStatus] = useState<AuthStatus>('loading');

  // Boot probe: enforcement flag + current session, in parallel. A failed config
  // fetch degrades to "not enforced"; a failed /me degrades to "logged out" — either
  // way the COP boots rather than trapping the operator behind a dead splash.
  useEffect(() => {
    const ac = new AbortController();
    let alive = true;
    void (async () => {
      const [config, me] = await Promise.all([
        fetchAuthConfig(ac.signal).catch(() => ({ authEnforced: false })),
        fetchAuthMe(ac.signal).catch(() => null),
      ]);
      if (!alive) return;
      setAuthEnforced(config.authEnforced);
      setUser(me);
      setStatus('ready');
    })();
    return () => {
      alive = false;
      ac.abort();
    };
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    const me = await fetchAuthMe().catch(() => null);
    setUser(me);
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<boolean> => {
      const result = await postLogin(email, password);
      if (!result.ok) return false;
      // Re-fetch /me to populate the full user (id/email/displayName) — login only
      // returns the display name.
      await refresh();
      return true;
    },
    [refresh],
  );

  const logout = useCallback(async (): Promise<void> => {
    await postLogout().catch(() => {});
    setUser(null);
  }, []);

  // A data-call 401 (session lapsed/revoked mid-session) clears identity, so the
  // gate falls to the login screen when enforcement is on.
  useEffect(() => {
    setUnauthorizedHandler(() => setUser(null));
    return () => setUnauthorizedHandler(null);
  }, []);

  const value: AuthState = { user, authEnforced, status, login, logout, refresh };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
